import { Container, Graphics, Sprite, Text } from 'pixi.js';
import { AssetLoader } from '../../asset/AssetLoader';
import type { PlinkoStep } from '../../model/plinko/PlinkoTypes';

const FONT_FAMILY = 'system-ui, -apple-system, sans-serif';
const PEG_RADIUS = 4;
const BALL_RADIUS = 8;
/** Default asset aliases — match the keys the host theme registers
 * under {@code SHARED_GAME_TEXTURES}. Engine widget checks for these
 * before falling back to the drawn-Graphics render path so engine-only
 * test scenes still read. */
const PEG_ALIAS = 'plinko:peg';
const BALL_ALIAS = 'plinko:ball';
const BIN_ALIAS = 'plinko:bin';
const CHUTE_ALIAS = 'plinko:chute';

export interface PlinkoBoardOptions {
  rows: number;
  /** Per-slot multipliers — length must be {@code rows + 1}. Drives the
   * slot bin labels + tints. */
  slotMultipliers: readonly number[];
  /** Vertical + horizontal spacing between adjacent pegs in px. Pegs
   * form a triangular grid where row r has r + 1 pegs. */
  pegSpacing: number;
  /** Pin colour. Default white, slightly transparent. */
  pegColor?: number;
  /** Ball fill colour. Default warm gold. */
  ballColor?: number;
  /** Border / accent colour for the board frame + slot stroke. */
  accentColor?: number;
  /** Slot bin tints — high-multiplier slots use the warm/red end of the
   * gradient, low slots use neutral. Falls back to a procedural gradient. */
  slotTints?: readonly number[];
}

/**
 * Plinko board widget — triangular peg field above a row of slot bins.
 * Owns the ball and provides {@link drop} which animates the ball along
 * a precomputed path before settling into the target slot.
 *
 * <p>Coordinate system: origin at the top-centre peg. Pegs at row r,
 * column c (0..r) sit at {@code (c - r/2) * spacing, r * spacing}.
 * Slots sit one full peg-spacing below the bottom peg row.
 */
export class PlinkoBoard extends Container {
  private readonly rows: number;
  private readonly spacing: number;
  private readonly slotMultipliers: readonly number[];
  private readonly slotTints: readonly number[];
  private readonly accentColor: number;
  /** Ball graphic — Sprite when {@code plinko:ball} texture loaded;
   * Graphics circle in the engine-only fallback. */
  private readonly ball: Container;
  /** Pegs render either as circles in {@link pegGraphics} (fallback)
   * or as individual Sprites in {@link pegContainer} (image mode). One
   * of the two is populated; the other stays empty. */
  private readonly pegGraphics: Graphics;
  private readonly pegContainer: Container;
  private readonly slotLayer: Container;
  /** Slot bin Graphics in fallback mode; left empty in image mode
   * (Sprites take their place but flashSlot still pulses them via
   * {@link slotPulseTargets}). */
  private readonly slotBins: Container[] = [];

  constructor(opts: PlinkoBoardOptions) {
    super();
    this.rows = opts.rows;
    this.spacing = opts.pegSpacing;
    this.slotMultipliers = opts.slotMultipliers;
    this.slotTints = opts.slotTints ?? defaultSlotTints(opts.slotMultipliers);
    this.accentColor = opts.accentColor ?? 0xff6bd6;

    // Optional commissioned chute sprite at the top of the board —
    // gives the ball a visible "drop point" when the asset is loaded.
    const chuteTex = AssetLoader.shared.getTexture(CHUTE_ALIAS);
    if (chuteTex) {
      const chute = new Sprite(chuteTex);
      chute.anchor.set(0.5, 1); // bottom-centre anchor — sits above row 0
      const aspect = chuteTex.width / chuteTex.height;
      chute.height = this.spacing * 1.2;
      chute.width = chute.height * aspect;
      chute.x = 0;
      chute.y = -this.spacing * 0.1;
      this.addChild(chute);
    }

    this.pegGraphics = new Graphics();
    this.pegContainer = new Container();
    this.addChild(this.pegGraphics);
    this.addChild(this.pegContainer);
    this.drawPegs(opts.pegColor ?? 0xffffff);

    this.slotLayer = new Container();
    this.addChild(this.slotLayer);
    this.drawSlots();

    const ballTex = AssetLoader.shared.getTexture(BALL_ALIAS);
    if (ballTex) {
      const sprite = new Sprite(ballTex);
      sprite.anchor.set(0.5);
      const aspect = ballTex.width / ballTex.height;
      // Sized as a fraction of peg spacing so the painted ball reads
      // cleanly without dwarfing the pegs.
      sprite.height = this.spacing * 0.55;
      sprite.width = sprite.height * aspect;
      this.ball = sprite;
    } else {
      const g = new Graphics();
      g.circle(0, 0, BALL_RADIUS)
        .fill({ color: opts.ballColor ?? 0xfff59d })
        .stroke({ color: 0xffffff, alpha: 0.85, width: 2 });
      this.ball = g;
    }
    this.ball.visible = false;
    this.addChild(this.ball);
  }

  /** Width of the visible peg field + slot bins. */
  totalWidth(): number {
    return (this.rows + 1) * this.spacing;
  }

  /** Height from the top peg to the bottom of the slot bins. */
  totalHeight(): number {
    return (this.rows + 1) * this.spacing + this.spacing * 0.9;
  }

  /** Drop the ball along {@code path} into the slot at {@code path}'s
   * net R-count. Resolves when the ball settles in the slot bin.
   * Caller controls per-hop duration via {@code hopDurationMs}. */
  async drop(path: readonly PlinkoStep[], hopDurationMs: number = 130): Promise<number> {
    let column = 0;
    this.ball.visible = true;
    this.ball.x = 0;
    this.ball.y = -this.spacing * 0.6; // start slightly above the top peg

    for (let r = 0; r < path.length; r++) {
      if (path[r] === 'R') column += 1;
      const targetX = (column - (r + 1) / 2) * this.spacing;
      const targetY = (r + 1) * this.spacing - this.spacing * 0.5;
      await this.tweenBallTo(targetX, targetY, hopDurationMs);
    }
    // Final settle into the slot bin.
    const slotIndex = column;
    const slotCenter = this.slotCenterX(slotIndex);
    const slotY = this.slotsTopY() + this.spacing * 0.45;
    await this.tweenBallTo(slotCenter, slotY, hopDurationMs * 1.6);

    // Pulse the landed slot bin briefly.
    this.flashSlot(slotIndex);
    return slotIndex;
  }

  /** Hide the ball — host calls this between drops. */
  resetBall(): void {
    this.ball.visible = false;
  }

  // -------- private --------

  private slotsTopY(): number {
    return (this.rows + 1) * this.spacing - this.spacing * 0.4;
  }

  private slotCenterX(slot: number): number {
    return (slot - this.rows / 2) * this.spacing;
  }

  private drawPegs(color: number): void {
    const pegTex = AssetLoader.shared.getTexture(PEG_ALIAS);
    if (pegTex) {
      // Image mode: one Sprite per peg, sized as a fraction of peg
      // spacing so the painted asset has visible detail without
      // crowding adjacent rows.
      const aspect = pegTex.width / pegTex.height;
      const pegSize = this.spacing * 0.45;
      for (let r = 0; r < this.rows; r++) {
        for (let c = 0; c <= r; c++) {
          const sprite = new Sprite(pegTex);
          sprite.anchor.set(0.5);
          sprite.height = pegSize;
          sprite.width = pegSize * aspect;
          sprite.x = (c - r / 2) * this.spacing;
          sprite.y = (r + 1) * this.spacing;
          this.pegContainer.addChild(sprite);
        }
      }
    } else {
      this.pegGraphics.clear();
      for (let r = 0; r < this.rows; r++) {
        for (let c = 0; c <= r; c++) {
          const x = (c - r / 2) * this.spacing;
          const y = (r + 1) * this.spacing;
          this.pegGraphics.circle(x, y, PEG_RADIUS).fill({ color, alpha: 0.85 });
        }
      }
    }
  }

  private drawSlots(): void {
    const top = this.slotsTopY();
    const slotHeight = this.spacing * 0.95;
    const binTex = AssetLoader.shared.getTexture(BIN_ALIAS);
    const binAspect = binTex ? binTex.width / binTex.height : 1;
    for (let i = 0; i <= this.rows; i++) {
      const cx = this.slotCenterX(i);
      const w = this.spacing * 0.92;
      const x = cx - w / 2;
      const tint = this.slotTints[i] ?? 0x6b3d77;
      let bin: Container;
      if (binTex) {
        // Image mode: one painted bin Sprite per slot. The painted
        // asset doesn't carry the per-slot multiplier tint — we apply
        // it via Pixi's tint property so high-multiplier slots still
        // read warmer than centre slots.
        const sprite = new Sprite(binTex);
        sprite.anchor.set(0.5);
        sprite.height = slotHeight;
        sprite.width = sprite.height * binAspect;
        sprite.x = cx;
        sprite.y = top + slotHeight / 2;
        sprite.tint = tint;
        bin = sprite;
      } else {
        const g = new Graphics();
        g.roundRect(x, top, w, slotHeight, 6)
          .fill({ color: tint, alpha: 0.85 })
          .stroke({ color: this.accentColor, alpha: 0.8, width: 2 });
        bin = g;
      }
      this.slotLayer.addChild(bin);
      this.slotBins.push(bin);

      const m = this.slotMultipliers[i] ?? 0;
      const label = new Text({
        text: formatMultiplier(m),
        style: {
          fontFamily: FONT_FAMILY,
          fontSize: Math.max(10, Math.floor(this.spacing * 0.32)),
          fontWeight: '900',
          fill: 0xffffff,
          align: 'center',
        },
      });
      label.anchor.set(0.5);
      label.x = cx;
      label.y = top + slotHeight / 2;
      this.slotLayer.addChild(label);
    }
  }

  private flashSlot(index: number): void {
    const bin = this.slotBins[index];
    if (!bin) return;
    const originalAlpha = bin.alpha;
    bin.alpha = 1;
    bin.scale.set(1.05);
    setTimeout(() => {
      bin.alpha = originalAlpha;
      bin.scale.set(1);
    }, 320);
  }

  private tweenBallTo(targetX: number, targetY: number, durationMs: number): Promise<void> {
    const startX = this.ball.x;
    const startY = this.ball.y;
    const arcHeight = Math.min(this.spacing * 0.35, durationMs * 0.18);
    return new Promise((resolve) => {
      const startTime = performance.now();
      const step = (now: number) => {
        const elapsed = now - startTime;
        const p = Math.min(1, elapsed / durationMs);
        const easedP = 1 - Math.pow(1 - p, 2); // easeOutQuad — feels like falling
        this.ball.x = startX + (targetX - startX) * easedP;
        const linearY = startY + (targetY - startY) * easedP;
        this.ball.y = linearY - Math.sin(p * Math.PI) * arcHeight;
        if (p < 1) {
          requestAnimationFrame(step);
        } else {
          resolve();
        }
      };
      requestAnimationFrame(step);
    });
  }
}

function formatMultiplier(m: number): string {
  if (m === 0) return '0';
  if (Number.isInteger(m)) return `${m}×`;
  return `${m.toFixed(1)}×`;
}

function defaultSlotTints(multipliers: readonly number[]): number[] {
  // High-multiplier slots tend to be at the edges; tint them warmer
  // (red/gold) and the centre slots cooler (purple). Linear interpolate
  // between the multiplier rank and the colour.
  const sorted = [...multipliers].sort((a, b) => b - a);
  const rankByValue = new Map<number, number>();
  sorted.forEach((v, i) => {
    if (!rankByValue.has(v)) rankByValue.set(v, i);
  });
  const max = sorted[0]!;
  const min = sorted[sorted.length - 1]!;
  const range = max - min || 1;
  return multipliers.map((m) => {
    const t = (m - min) / range; // 0..1
    return interpolateColour(0x6b3d77, 0xff3b6b, t);
  });
}

function interpolateColour(low: number, high: number, t: number): number {
  const lr = (low >> 16) & 0xff;
  const lg = (low >> 8) & 0xff;
  const lb = low & 0xff;
  const hr = (high >> 16) & 0xff;
  const hg = (high >> 8) & 0xff;
  const hb = high & 0xff;
  const r = Math.round(lr + (hr - lr) * t);
  const g = Math.round(lg + (hg - lg) * t);
  const b = Math.round(lb + (hb - lb) * t);
  return (r << 16) | (g << 8) | b;
}
